// src/components/HeroBanner.jsx
import React, { useEffect, useState } from 'react';
import Hero from './Hero';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

const DATA_URL =
  'https://raw.githubusercontent.com/gyanesh-singh0/json-configs/refs/heads/main/homepage.json';

const normalizeMainImage = (mainImageObj) => {
  if (!mainImageObj) return { banners: [], phoneBanners: [] };

  // try to get numeric length; JSON sometimes uses a string
  const len = parseInt(mainImageObj.length, 10) || (() => {
    // fallback: find max numeric key present
    const keys = Object.keys(mainImageObj);
    let max = 0;
    keys.forEach(k => {
      const n = Number(k);
      if (!Number.isNaN(n) && Number.isInteger(n)) max = Math.max(max, n);
    });
    return max;
  })();

  const banners = [];
  const phoneBanners = [];

  for (let i = 1; i <= len; i++) {
    const idx = i.toString();

    // image keys - prefer `i` (e.g. "1", "2"), fallback to other keys if needed
    const desktopImage = mainImageObj[idx] || mainImageObj[`image${idx}`] || null;
    const phoneImage = mainImageObj[`phone${idx}`] || desktopImage;

    // title keys: try several possibilities (your JSON has both `title2` and `mainImage_title2`)
    const title =
      mainImageObj[`title${idx}`] ||
      mainImageObj[`mainImage_title${idx}`] ||
      mainImageObj[`mainImage_title${i}`] ||
      mainImageObj[`mainImage_title${idx}`] ||
      null;

    // genre may not exist in this JSON
    const genre = mainImageObj[`genre${idx}`] || mainImageObj[`mainImage_genre${idx}`] || null;

    // rating / more info
    const rating = mainImageObj[`moreinfo${idx}`] || mainImageObj[`moreInfo${idx}`] || null;

    // id code
    const id =
      mainImageObj[`mainImage_code${idx}`] ||
      mainImageObj[`id${idx}`] ||
      // fallback to index if no id found
      `banner-${idx}`;

    // only push if there's at least an image or title
    if (desktopImage || title) {
      banners.push({
        id,
        image: desktopImage,
        title,
        genre,
        rating,
      });

      phoneBanners.push({
        id,
        image: phoneImage,
        title,
        genre,
        rating,
      });
    }
  }

  return { banners, phoneBanners };
};

const HeroBanner = () => {
  const [banners, setBanners] = useState([]);
  const [phoneBanners, setPhoneBanners] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        const res = await fetch(DATA_URL, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to fetch banner JSON: ${res.status}`);
        const json = await res.json();
        const mainImageObj = json?.MainImage || json?.mainImage || null;
        const { banners: mapped, phoneBanners: mappedPhone } = normalizeMainImage(mainImageObj);

        if (!cancelled) {
          setBanners(mapped);
          setPhoneBanners(mappedPhone);
        }
      } catch (err) {
        console.error('HeroBanner load error:', err);
        if (!cancelled) setError(err.message || 'Unknown error');
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  // Optionally show nothing or a small placeholder while loading.
  // Here we render nothing if no banners loaded.
  if (error) {
    return <div className="text-center py-6">Failed to load banners: {error}</div>;
  }

  if (!banners.length && !phoneBanners.length) {
    return null; // or a loader/spinner if you prefer
  }

return (
  <div className="relative block md:hidden">

    {/* Top-right backup button */}
<a
  href="https://pikchar.xyz"
  target="_blank"
  rel="noopener noreferrer"
  className="absolute top-4 right-4 z-50
             bg-purple-600 text-white
             text-sm md:text-base
             px-4 py-2 rounded-md
             hover:bg-purple-700
             transition"
>
  Backup – pikchar.xyz
</a>


    {/* Desktop banner */}
    <div className="hidden md:block">
      <Swiper
        modules={[A11y, Autoplay]}
        slidesPerView={1}
        loop={true}
        autoplay={{ delay: 5000 }}
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.id}>
            <Hero {...banner} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>

    {/* Mobile banner */}
    <div className="block md:hidden">
      <Swiper
        modules={[A11y, Autoplay]}
        slidesPerView={1}
        loop={true}
        autoplay={{ delay: 5000 }}
        style={{ height: '40vh' }}
      >
        {phoneBanners.map((banner) => (
          <SwiperSlide key={banner.id}>
            <Hero {...banner} isMobile={true} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  </div>
);

};

export default HeroBanner;